import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { adminApi } from "../../lib/api";
import CrudManager from "./CrudManager";
import { GoogleBusinessConnect, GbpPostButton } from "./GoogleBusinessManager";
import { MetricoolPostButton } from "./MetricoolComposer";
import { MetricoolRecurringButton } from "./RecurringPromos";

export default function BlogManager() {
  const [guideOptions, setGuideOptions] = useState([{ value: "none", label: "— None (use a default guide) —" }]);

  useEffect(() => {
    adminApi.guides.list()
      .then((gs) => setGuideOptions([
        { value: "none", label: "— None (use a default guide) —" },
        ...(gs || []).map((g) => ({ value: g.id, label: g.title })),
      ]))
      .catch(() => {});
  }, []);

  return (
    <>
      <GoogleBusinessConnect />
      <CrudManager
        label="Blog Post"
        api={adminApi.blog}
        rowExtra={(item, reload) => {
          const origin = typeof window !== "undefined" ? window.location.origin : "";
          const link = item.slug ? `${origin}/api/share/blog/${item.slug}` : origin;
          const caption = `${item.title || ""}\n\n${item.excerpt ? item.excerpt + "\n\n" : ""}Read more: ${link}`.trim();
          return (
            <div className="flex items-center gap-2">
              <MetricoolPostButton item={item} />
              <MetricoolRecurringButton initial={{ text: caption, title: item.title || "", summary: item.excerpt || "", link, kind: "blog", autogen: true, image_url: item.image || "", source: "blog", source_id: item.id }} />
              <GbpPostButton item={item} onDone={reload} />
            </div>
          );
        }}
        makeEmpty={() => ({ title: "", category: "General", excerpt: "", image: "", youtube_id: "", author: "The CASS Team", date: "", content: [], content_html: "", published: true, guide_id: "none" })}
        renderRow={(i) => {
          const today = new Date().toISOString().slice(0, 10);
          const scheduled = i.published && i.date && i.date > today;
          const badge = !i.published
            ? { text: "Draft", tone: "slate" }
            : scheduled
            ? { text: `Scheduled · ${i.date}`, tone: "amber" }
            : { text: "Published", tone: "green" };
          return { title: i.title, subtitle: `${i.category} • ${i.date || ""}`, image: i.image, badge };
        }}
        banner={(form) => {
          const today = new Date().toISOString().slice(0, 10);
          if (form.published && form.date && form.date > today) {
            return (
              <div data-testid="blog-scheduled-banner" className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-amber-50 border-y border-amber-200 flex items-center gap-2 text-amber-800 text-sm font-semibold">
                <Clock size={16} />
                Scheduled — this post stays hidden on the site until {new Date(form.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.
              </div>
            );
          }
          return null;
        }}
        fields={[
          { name: "title", label: "Title", type: "text" },
          { name: "category", label: "Category", type: "text", placeholder: "e.g. Tax Planning" },
          { name: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
          { name: "image", label: "Cover Image", type: "image", alt: "image_alt" },
          { name: "youtube_id", label: "YouTube video (optional)", type: "text", hint: "Paste a YouTube link or ID to embed a player at the top of the article (replaces the cover image). Auto-filled for podcast episode posts." },
          { name: "guide_id", label: "Matching free guide (optional)", type: "select", options: guideOptions, hint: "Shows a sticky 'Download the matching guide' button on this article. Leave as None to fall back to a default guide automatically." },
          { name: "author", label: "Author", type: "text" },
          { name: "date", label: "Publish date", type: "date", hint: "Set a future date to schedule the post — it stays hidden on the live site until this date, then goes live automatically. Leave blank to publish immediately." },
          { name: "content_html", label: "Content", type: "richtext", templateKind: "blog", convertFrom: "content", hint: "Use the toolbar for bold, headings, lists, quotes and links (internal e.g. /services/payroll or external URLs). Tip: 'Start from a template' drops in an on-brand skeleton you can fill in." },
          { name: "published", label: "Published (visible on site)", type: "switch" },
        ]}
      />
    </>
  );
}
